import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { PersistedTimer, TimerStoreService } from '../../services/timer-store.service';
import { TimeSheetService } from '../../../time-sheet/services/time-sheet.service';
import { ITimeSheetRequest } from '../../../time-sheet/models/ITimeSheetRequest.interface';
import {
  TimerStopDialogComponent,
  TimerStopDialogResult,
} from '../timer-stop-dialog/timer-stop-dialog.component';
import { ConfirmDialogComponent } from '../../../corporate/confirm-dialog/confirm-dialog.component';
import { NavLinkDirective } from '../../context-menu/nav-link.directive';

interface TimerVM extends PersistedTimer {
  elapsedSeconds: number;
  elapsedLabel: string;
  /** True when we have everything needed to save a timesheet without opening the source screen. */
  canStopHeadless: boolean;
}

/**
 * Always-on floating widget that lists every running timer (across notes, the
 * dashboard, and timesheet rows). For note timers with a complete context it can
 * Stop & save the timesheet in place; otherwise Stop just navigates back to the
 * source screen so the user can finish there. Reads everything from
 * {@link TimerStoreService}, so it works regardless of which screen is open.
 */
@Component({
    selector: 'app-active-timers',
    imports: [CommonModule, MatIconModule, MatTooltipModule, NavLinkDirective],
    templateUrl: './active-timers.component.html',
    styleUrls: ['./active-timers.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActiveTimersComponent {
  private readonly timerStore = inject(TimerStoreService);
  private readonly timeSheetService = inject(TimeSheetService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  collapsed = false;

  // Re-evaluate every second so the elapsed labels tick — but only while a timer is running. This
  // widget is on every page for every signed-in user, and each tick is an app-wide change detection
  // pass (Zone.js), so ticking with nothing to show re-checked the whole screen once a second.
  readonly vm$ = this.timerStore.timers$.pipe(
    switchMap(timers => timers.length
      ? timer(0, 1000).pipe(map(() => timers))
      : of(timers)),
    map(timers => timers
      .map(t => this.toVm(t))
      .sort((a, b) => a.startTimeIso.localeCompare(b.startTimeIso))),
  );

  trackByKey = (_: number, t: TimerVM) => t.key;

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  iconFor(kind?: string): string {
    switch (kind) {
      case 'dashboard': return 'space_dashboard';
      case 'timesheet-row': return 'table_rows';
      default: return 'sticky_note_2';
    }
  }

  /** Where goTo lands, for the right-click menu. */
  timerLink(t: TimerVM): string | null {
    if (!t.returnUrl) return null;
    const [path, query] = t.returnUrl.split('?');
    const queryParams = this.parseQuery(query);
    return this.router.serializeUrl(
      this.router.createUrlTree([path], Object.keys(queryParams).length ? { queryParams } : {}));
  }

  /** Jump back to the screen the timer was started from (same tab). */
  goTo(t: TimerVM): void {
    if (!t.returnUrl) return;
    const [path, query] = t.returnUrl.split('?');
    const queryParams = this.parseQuery(query);
    this.router.navigate([path], Object.keys(queryParams).length ? { queryParams } : {});
  }

  /** Open the source note/screen in a new browser tab. */
  openInNewTab(t: TimerVM): void {
    if (!t.returnUrl || typeof window === 'undefined') return;
    window.open(`${window.location.origin}${this.appPath(t.returnUrl)}`, '_blank');
  }

  /** Stop the timer: confirm details and save in place when possible, otherwise return to the source screen. */
  stop(t: TimerVM): void {
    // Any note timer with a known client can be saved from here. Service-less notes
    // lack a department/service, so the dialog collects them before we build the row.
    const ctx = t.context;
    if (t.kind === 'note' && ctx && (!!ctx.businessId || !!ctx.individualId)) {
      const needsDepartmentService = !ctx.departmentId || !ctx.serviceId;
      this.dialog.open(TimerStopDialogComponent, {
        autoFocus: false,
        data: {
          label: t.label,
          reference: t.reference,
          elapsedLabel: t.elapsedLabel,
          billable: t.billable,
          description: t.description,
          needsDepartmentService,
          noteCompanyId: ctx.noteCompanyId ?? null,
          formId: ctx.formId ?? null,
          noteId: ctx.noteId ?? null,
        },
      }).afterClosed().subscribe((result: TimerStopDialogResult | null) => {
        if (result) {
          this.saveNoteTimesheet(t, result);
        }
      });
      return;
    }
    // Dashboard timer opens a client-picker dialog, timesheet rows write into a
    // form — both need their own screen. Flag the timer so the source screen opens
    // its save dialog, then make sure that screen is showing.
    this.snackBar.open('Opening the timer to finish saving…', 'Close', { duration: 3000 });
    this.timerStore.requestStop(t.key);
    this.goTo(t);
  }

  /** Discard a timer without saving any time. */
  discard(t: TimerVM): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Discard timer',
        text: `Discard the timer for "${t.label || 'this item'}" (${t.elapsedLabel})? The tracked time will not be saved.`,
        subText: 'Discard',
      },
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.timerStore.clear(t.key);
      }
    });
  }

  private saveNoteTimesheet(t: TimerVM, result: TimerStopDialogResult): void {
    const ctx = t.context!;
    const start = new Date(t.startTimeIso);
    const end = new Date();
    const userComment = result.description?.trim() || '';
    const autoComment = `Meeting Note: ${ctx.referenceNumber || ''}${t.returnUrl ? ` | ${window.location.origin}${this.appPath(t.returnUrl)}` : ''}`;
    const comments = userComment ? `${userComment} | ${autoComment}` : autoComment;

    const request: ITimeSheetRequest = {
      date: formatDate(start, 'yyyy-MM-dd', 'en-US'),
      serviceId: (ctx.serviceId ?? result.serviceId)!,
      departmentId: (ctx.departmentId ?? result.departmentId)!,
      // `||`, not `??`: note form data carries 0 for the absent customer id, and sending both
      // ids fails the API's "exactly one of IndividualId / BusinessId" check.
      individualId: (ctx.individualId || null) as any,
      businessId: (ctx.businessId || null) as any,
      organizationCompanyId: null as any,
      description: ctx.title || 'Meeting Note',
      startTime: this.formatTimeForApi(start),
      endTime: this.formatTimeForApi(end),
      billable: result.billable,
      comments,
      referenceNumber: ctx.referenceNumber || undefined,
      year: ctx.year || new Date().getFullYear(),
      attendees: result.attendees || [],
    };

    // Use next/error rather than a null sentinel: a successful save can return an
    // empty body, which must NOT be mistaken for a failure (that left the timer
    // running even though the entry was saved).
    this.timeSheetService.createTimerEntries(request, start, end).subscribe({
      next: () => {
        const extra = (result.attendees?.length || 0) > 0 ? ` (+${result.attendees.length} attendee(s))` : '';
        this.snackBar.open(`Timesheet entry created: ${t.elapsedLabel}${extra}`, 'Close', { duration: 5000 });
        this.timerStore.clear(t.key);
      },
      error: (err) => {
        const reason = err?.error?.message || err?.error?.detail || err?.error?.title
          || (typeof err?.error === 'string' ? err.error : '');
        const msg = reason
          ? `Could not save the timesheet entry: ${reason} Timer kept running.`
          : 'Could not save the timesheet entry. Timer kept running — try again.';
        this.snackBar.open(msg, 'Close', { duration: 6000 });
        // Leave the timer running so the user can retry.
      },
    });
  }

  private toVm(t: PersistedTimer): TimerVM {
    const start = new Date(t.startTimeIso).getTime();
    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - start) / 1000));
    const ctx = t.context;
    const canStopHeadless = !!ctx
      && !!ctx.departmentId
      && !!ctx.serviceId
      && (!!ctx.businessId || !!ctx.individualId);
    return {
      ...t,
      elapsedSeconds,
      elapsedLabel: this.formatElapsed(elapsedSeconds),
      canStopHeadless,
    };
  }

  private formatElapsed(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }

  private formatTimeForApi(date: Date): string {
    return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`;
  }

  private appPath(returnUrl: string): string {
    return returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`;
  }

  private parseQuery(query?: string): Record<string, string> {
    const params: Record<string, string> = {};
    if (!query) return params;
    for (const pair of query.split('&')) {
      const [k, v] = pair.split('=');
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v || '');
    }
    return params;
  }
}
